import { Injectable, inject, signal, WritableSignal } from '@angular/core';
import { InjectionToken } from '@angular/core';
import { AuthenticationService } from '../core/providers';
import { Subject } from 'rxjs';
import cloneDeep from 'lodash/cloneDeep';

// import * as Sentry from '@sentry/angular';
import { ClientBrandingApiService } from './client-branding-api.service';

type InjectionWindow = Window & typeof globalThis;

export const LOGO_SIZING_CONFIG = '0x42xc';

export const CSS_NAME_CACHE = new Map();

/**
 * Create a css variable name with kebab case and prefix.
 * @param str
 * @param prefix
 */
export function getCssVarName(
  str: string,
  prefix: string = 'rdo-branding'
): string {
  if (CSS_NAME_CACHE.has(str)) {
    return CSS_NAME_CACHE.get(str);
  }
  const result = `--${prefix}-${str.split('_').join('-')}`;
  CSS_NAME_CACHE.set(str, result);
  return result;
}

/**
 * Get the css var string with a default color in case the variable doesn't exist.
 * @param key
 * @param defaultColor
 */
export function getClientBrandingCssVarString(
  key: 'primary_chart_color' | 'secondary_chart_color',
  defaultColor: HexString
): string {
  return `var(${getCssVarName(key)}, ${defaultColor})`;
}

/**
 * Add the size string to the logo url returned from api.
 * @param url
 * @param sizeStr
 */
export function updateLogoSize(
  url: string | null,
  sizeStr = LOGO_SIZING_CONFIG
): string | null {
  if (!url) {
    return null;
  }
  const parts = url.split('.');
  const fileExt = parts.pop();
  const removedSize = parts.pop();
  return `${parts.join('.')}.${sizeStr}.${fileExt}`;
}

/**
 * This is an artifact of writing tests and having trouble mocking the window. Leaving in for now.
 */
export const WINDOW: InjectionToken<InjectionWindow> =
  new InjectionToken<InjectionWindow>('Global window object', {
    factory: () => window,
  });

@Injectable({
  providedIn: 'root',
})
export class ClientBrandingService {
  public isLoaded = signal(false);
  public data: any = signal(null);
  public window: (Window & typeof globalThis) | null;
  public isReady: Subject<boolean> = new Subject();
  readonly logoUrl: WritableSignal<string | null> = signal(null);

  constructor(
    public http: ClientBrandingApiService,
    public authenticationService: AuthenticationService
  ) {
    this.window = inject(WINDOW);
    this.authenticationService.Authenticated.subscribe((isAuthenticated) => {
      if (isAuthenticated) {
        this.load();
      }
    });
  }

  public async preloadLogo(): Promise<void> {
    const url = this.logoUrl();
    if (!url) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = url;
      img.onload = () => {
        resolve();
      };
      img.onerror = (err) => {
        console.error(err);
        reject(err);
      };
    });
  }

  private load(): void {
    this.http.get().subscribe(async (resp: any) => {
      try {
        this.data.set(resp?.data);
        this.setLogoUrl();
        await this.preloadLogo();
      } catch (error) {
        console.error(error);
        // remove the logo url reference if the download fails.
        const dataCopy = cloneDeep(this.data());
        delete dataCopy.files;
        this.logoUrl.set(null);
        this.data.set(dataCopy);
      } finally {
        this.isLoaded.set(true);
        this.isReady.next(true);
        this.isReady.complete();
      }
    });
  }

  private setLogoUrl(): void {
    const data = this.data();
    const url = data?.files && data?.files[0] ? data.files[0].url : null;
    if (!url) {
      return;
    }
    this.logoUrl.set(updateLogoSize(url));
  }
}
